/**
 * Instagram Story share utility.
 * Full 9:16 story canvas with background; content drawn in a centered rounded box.
 * Fallback: download image if Web Share API is not supported.
 */

// Standard Instagram story viewport (9:16)
const VIEWPORT_WIDTH = 1080;
const VIEWPORT_HEIGHT = 1920;

// Centered content box (card) — larger for better readability and focus
const BOX_WIDTH = 1000;
const BOX_HEIGHT = 760;
const BOX_RADIUS = 40;
const SAFE_PADDING = 56;

const BRAND_NAME = "لنزسه";
const BRAND_SUBTITLE = "پلتفرم اجتماعی ورزش";
const LOGO_URL = "/logo.png";
const STORY_BG_URL = "/bg-story.png?v=6";
const BRAND_BG = "#0f172a"; // slate-900
const BRAND_ACCENT = "#f8fafc"; // slate-50
const TEXT_COLOR = "#f1f5f9";
const MUTED_COLOR = "#94a3b8";
const STORY_BG_FALLBACK = "#0f172a"; // fallback if bg image fails to load
const VERIFIED_BADGE_COLOR = "#3b82f6"; // blue-500

export type StoryShareContent = {
  /** Post or card text (main content) */
  text: string;
  /** Optional author display name */
  authorName?: string;
  /** Optional profile picture URL (full URL for canvas loading) */
  profilePictureUrl?: string;
  /** Whether to show verification badge next to author */
  isVerified?: boolean;
  /** Optional post URL for "See more" */
  url?: string;
  /** Max length for displayed text (truncate with …) */
  maxTextLength?: number;
};

const DEFAULT_MAX_TEXT = 180;

/** Timeout (ms) for loading profile images — avoid hanging when CORS fails (e.g. local dev).
 * For CDN profile URLs, callers should use the backend /api/proxy-image so the image is same-origin and loads without CORS. */
const PROFILE_IMAGE_LOAD_TIMEOUT = 4000;

/**
 * Loads an image from URL; resolves with HTMLImageElement or null on error.
 * Uses CORS so canvas can export; if the server does not allow CORS, use fallback avatar.
 * Optional timeout (ms) to avoid hanging when load fails (e.g. CORS in local dev).
 */
function loadImage(src: string, timeoutMs?: number): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    let settled = false;
    const done = (result: HTMLImageElement | null) => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    img.onload = () => done(img);
    img.onerror = () => done(null);
    if (timeoutMs != null && timeoutMs > 0) {
      setTimeout(() => done(null), timeoutMs);
    }
    img.src = src;
  });
}

/**
 * Draws a fallback avatar circle with initial letter (when profile image fails to load).
 */
function drawFallbackAvatar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  radius: number,
  initial: string
): void {
  ctx.save();
  const gradient = ctx.createLinearGradient(x - radius, y - radius, x + radius, y + radius);
  gradient.addColorStop(0, "#3b82f6");
  gradient.addColorStop(1, "#9333ea");
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.fillStyle = "#fff";
  ctx.font = `600 ${radius * 1.1}px system-ui, -apple-system, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.direction = "ltr";
  ctx.fillText((initial || "?").toUpperCase(), x, y);
  ctx.restore();
}

/**
 * Wraps and draws text on canvas with RTL support and safe width.
 */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number
): number {
  ctx.direction = "rtl";
  ctx.textAlign = "right";
  const words = text.replace(/\n/g, " ").split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    const metrics = ctx.measureText(next);
    if (metrics.width <= maxWidth) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }
  if (current) lines.push(current);

  const toDraw = lines.slice(0, maxLines);
  toDraw.forEach((line, i) => {
    ctx.fillText(line, x, y + i * lineHeight);
  });
  return toDraw.length * lineHeight;
}

/**
 * Truncates text to max length with ellipsis.
 */
function truncateText(text: string, maxLen: number): string {
  const t = (text || "").trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen - 1).trim() + "…";
}

// Heroicons "check badge" path (viewBox 0 0 20 20) — shield with checkmark
const VERIFIED_BADGE_PATH =
  "M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z";

/**
 * Draws a verification badge (shield with checkmark icon) at the given position.
 * Uses the same icon as the app's VerificationBadge component for consistency.
 */
function drawVerificationBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number
): void {
  ctx.save();
  const scale = size / 20;
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.translate(-10, -10);
  const path = new Path2D(VERIFIED_BADGE_PATH);
  ctx.fillStyle = VERIFIED_BADGE_COLOR;
  ctx.fill(path);
  ctx.restore();
}

/**
 * Adds a rounded rectangle path to the context (does not fill or stroke).
 */
function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number
): void {
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
}

/**
 * Renders the story card to a canvas and returns a PNG Blob.
 * Full viewport = standard Instagram story (1080×1920); content box is centered.
 */
export async function renderStoryCanvas(content: StoryShareContent): Promise<Blob> {
  const canvas = document.createElement("canvas");
  canvas.width = VIEWPORT_WIDTH;
  canvas.height = VIEWPORT_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2d context not available");

  const maxTextLen = content.maxTextLength ?? DEFAULT_MAX_TEXT;
  const displayText = truncateText(content.text, maxTextLen);

  // Full-viewport background: bg-story.png (cover, centered); fallback solid if load fails
  const bgImage = await loadImage(STORY_BG_URL);
  if (bgImage) {
    const scale = Math.max(
      VIEWPORT_WIDTH / bgImage.naturalWidth,
      VIEWPORT_HEIGHT / bgImage.naturalHeight
    );
    const sw = bgImage.naturalWidth;
    const sh = bgImage.naturalHeight;
    const dw = sw * scale;
    const dh = sh * scale;
    const dx = (VIEWPORT_WIDTH - dw) / 2;
    const dy = (VIEWPORT_HEIGHT - dh) / 2;
    ctx.drawImage(bgImage, 0, 0, sw, sh, dx, dy, dw, dh);
  } else {
    ctx.fillStyle = STORY_BG_FALLBACK;
    ctx.fillRect(0, 0, VIEWPORT_WIDTH, VIEWPORT_HEIGHT);
  }

  // Centered content box (rounded card)
  const boxX = (VIEWPORT_WIDTH - BOX_WIDTH) / 2;
  const boxY = (VIEWPORT_HEIGHT - BOX_HEIGHT) / 2;
  ctx.beginPath();
  roundedRectPath(ctx, boxX, boxY, BOX_WIDTH, BOX_HEIGHT, BOX_RADIUS);
  ctx.fillStyle = BRAND_BG;
  ctx.fill();

  // Draw all content inside the box (coordinates relative to box)
  ctx.save();
  ctx.translate(boxX, boxY);
  ctx.beginPath();
  roundedRectPath(ctx, 0, 0, BOX_WIDTH, BOX_HEIGHT, BOX_RADIUS);
  ctx.clip();

  ctx.fillStyle = BRAND_BG;
  ctx.fillRect(0, 0, BOX_WIDTH, BOX_HEIGHT);

  const centerX = BOX_WIDTH / 2;
  const contentLeft = SAFE_PADDING;
  const contentRight = BOX_WIDTH - SAFE_PADDING;
  const contentWidth = contentRight - contentLeft;

  // Logo at top (compact header so main content is the focus)
  const logoSize = 64;
  const logoY = SAFE_PADDING + 16;
  const logo = await loadImage(LOGO_URL);
  if (logo) {
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, logoY + logoSize / 2, logoSize / 2, 0, Math.PI * 2);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(logo, centerX - logoSize / 2, logoY, logoSize, logoSize);
    ctx.restore();
  }

  // Brand name under logo — readable but not dominant
  const brandY = logoY + logoSize + 20;
  ctx.font = "600 32px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = BRAND_ACCENT;
  ctx.direction = "rtl";
  ctx.textAlign = "center";
  ctx.fillText(BRAND_NAME, centerX, brandY);
  ctx.font = "400 20px system-ui, -apple-system, sans-serif";
  ctx.fillStyle = MUTED_COLOR;
  ctx.fillText(BRAND_SUBTITLE, centerX, brandY + 32);

  // Divider line
  const dividerY = brandY + 78;
  ctx.strokeStyle = "rgba(248, 250, 252, 0.25)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(contentLeft, dividerY);
  ctx.lineTo(contentRight, dividerY);
  ctx.stroke();

  const mainContentFontSize = 42;
  const lineHeight = 70;
  const maxLines = 5;
  const authorRowGap = 32;
  const contentTop = dividerY + 80;

  // Main content (post text) first — large, high contrast, easy to read
  ctx.font = `500 ${mainContentFontSize}px system-ui, -apple-system, sans-serif`;
  ctx.fillStyle = TEXT_COLOR;
  const textHeight = drawWrappedText(
    ctx,
    displayText,
    contentRight,
    contentTop,
    contentWidth,
    lineHeight,
    maxLines
  );

  // Author row at bottom (after text): profile picture, author name, verification badge
  const hasAuthor = !!(content.authorName || content.profilePictureUrl);
  if (hasAuthor) {
    const authorRowY = contentTop + textHeight + authorRowGap;
    const avatarSize = 48;
    const avatarX = contentRight - avatarSize / 2;
    const avatarY = authorRowY + avatarSize / 2;

    // Profile picture (circle) or fallback avatar with initial.
    // Use timeout so we don't hang when load fails (e.g. CORS in local dev); then fallback is used.
    const authorInitial = (content.authorName || "?").charAt(0);
    let avatarDrawn = false;
    if (content.profilePictureUrl) {
      const profileImg = await loadImage(
        content.profilePictureUrl,
        PROFILE_IMAGE_LOAD_TIMEOUT
      );
      if (profileImg) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarSize / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(
          profileImg,
          avatarX - avatarSize / 2,
          authorRowY,
          avatarSize,
          avatarSize
        );
        ctx.restore();
        avatarDrawn = true;
        // Subtle ring
        ctx.beginPath();
        ctx.arc(avatarX, avatarY, avatarSize / 2, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(248, 250, 252, 0.3)";
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
    if (!avatarDrawn) {
      drawFallbackAvatar(ctx, avatarX, avatarY, avatarSize / 2, authorInitial);
      ctx.beginPath();
      ctx.arc(avatarX, avatarY, avatarSize / 2, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(248, 250, 252, 0.25)";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Author name (right of avatar in RTL = to the left of avatar in coords)
    if (content.authorName) {
      const nameX = contentRight - avatarSize - 16;
      const nameY = authorRowY + avatarSize / 2 + 8;
      ctx.font = "500 26px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = MUTED_COLOR;
      ctx.direction = "rtl";
      ctx.textAlign = "right";
      const authorText = `— ${content.authorName}`;
      ctx.fillText(authorText, nameX, nameY);
      const nameWidth = ctx.measureText(authorText).width;

      // Verification badge next to name (to the left of name in RTL)
      if (content.isVerified) {
        const badgeSize = 28;
        const badgeX = nameX - nameWidth - badgeSize / 2 - 8;
        const badgeY = (authorRowY + 2) + avatarSize / 2;
        drawVerificationBadge(ctx, badgeX, badgeY, badgeSize);
      }
    } else if (content.isVerified && content.profilePictureUrl) {
      // Badge on avatar when no author name
      const badgeSize = 22;
      const badgeX = avatarX + avatarSize / 2 - 4;
      const badgeY = authorRowY + avatarSize - 8;
      drawVerificationBadge(ctx, badgeX, badgeY, badgeSize);
    }
  }


  ctx.restore();

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Canvas toBlob failed"))),
      "image/png",
      0.95
    );
  });
}

/**
 * Shares the story image via Web Share API (Instagram Story on supported devices).
 * Falls back to downloading the image if share is not available or user cancels.
 */
export async function shareToInstagramStory(content: StoryShareContent): Promise<{
  shared: boolean;
  fallbackDownload?: boolean;
  error?: string;
}> {
  try {
    const blob = await renderStoryCanvas(content);
    const file = new File([blob], "lenz3-story.png", { type: "image/png" });

    // Share ONLY the image file. On mobile (especially iOS), passing files + text/url/title
    // causes canShare() to be false or share to fail; Instagram Story only needs the image.
    // Text, author and URL are already rendered on the canvas.
    const shareData: ShareData = { files: [file] };

    if (typeof navigator !== "undefined" && navigator.share && navigator.canShare?.(shareData)) {
      await navigator.share(shareData);
      return { shared: true };
    }

    // Fallback: download
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "lenz3-story.png";
    a.click();
    URL.revokeObjectURL(url);
    return { shared: false, fallbackDownload: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Share failed";
    if ((err as Error)?.name === "AbortError") {
      return { shared: false };
    }
    return { shared: false, error: message };
  }
}
